import { useTranslation } from 'react-i18next';
import { FormLabel, Tooltip } from '@material-ui/core';
import makeStyles from '@material-ui/styles/makeStyles';
import InfoIcon from '@material-ui/icons/Info';
import customCheckboxRadioSwitch from 'assets/jss/material-dashboard-pro-react/customCheckboxRadioSwitch';
import EXTERNAL_URLS from 'constants/externalUrls';
import FAKTUROWNIA_INTEGRATION_TYPES from 'constants/fakturowniaIntegrationTypes';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import FormTextInputNoGrid from 'components/FormTextInput/FormTextInputNoGrid';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import InfoWithTooltip from 'components/InfoWithTooltip/InfoWithTooltip';
import LabeledCheckbox from 'components/Checkbox/LabeledCheckbox';
import SelectInput from 'components/FormSelect/SelectInput';

const useStyles = makeStyles({
  inputGridItem: {
    height: '65px',
  },
  integrationTypeLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  noRightMargin: {
    marginRight: '0',
  },
  infoIcon: {
    marginLeft: '4px',
    fontSize: '16px',
  },
  gridItemsWrapper: {
    width: '100%',
    margin: '10px',
    border: '1px solid #cfc9c9',
  },
  checkboxWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  checkbox: {
    padding: '11px 14px 9px 0',
  },
  labelHorizontal: customCheckboxRadioSwitch.labelHorizontal,
});

const FakturowniaModule = ({ handleModuleUpdate, module }) => {
  const { t } = useTranslation();

  const classes = useStyles();

  const invoiceDatesOptions = [
    {
      label: t('module.Fakturownia.invoiceDatesOptions.orderDate'),
      value: 'ORDER_DATE',
    },
    {
      label: t('module.Fakturownia.invoiceDatesOptions.lastBagDate'),
      value: 'LAST_BAG_DATE',
    },
  ];

  const isIntegrationTypeDisabled =
    module.integrationType === FAKTUROWNIA_INTEGRATION_TYPES.disabled;

  if (!module) {
    return (
      <div>
        {t('brands.newBrandForm.moduleNotLoaded', {
          moduleName: 'FakturowniaModule',
        })}
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardBody>
          <GridContainer>
            <GridItem md={12}>
              <LabeledCheckbox
                className={classes.noRightMargin}
                label={t('brands.newBrandForm.showOption')}
                checked={module.allowInvoicesForClient}
                onChange={() => {
                  handleModuleUpdate({
                    ...module,
                    allowInvoicesForClient: !module.allowInvoicesForClient,
                  });
                }}
              />
              <Tooltip
                title={t(
                  'module.Fakturownia.addonsCustomName.showOptionInvoiceTooltip'
                )}
                placement="top"
              >
                <InfoIcon className={classes.infoIcon} />
              </Tooltip>
            </GridItem>
          </GridContainer>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <GridContainer>
            <GridItem md={12}>
              <LabeledCheckbox
                label={
                  <span className={classes.integrationTypeLabel}>
                    Fakturownia.pl
                    <InfoWithTooltip
                      title={t('brands.newBrandForm.fakturownia.systemConfig')}
                      link={EXTERNAL_URLS.invoiceConfig}
                      size="s"
                    />
                  </span>
                }
                checked={
                  module.integrationType ===
                    FAKTUROWNIA_INTEGRATION_TYPES.advance ||
                  module.integrationType ===
                    FAKTUROWNIA_INTEGRATION_TYPES.invoice ||
                  module.integrationType ===
                    FAKTUROWNIA_INTEGRATION_TYPES.receipt
                }
                onChange={() => {
                  handleModuleUpdate({
                    ...module,
                    integrationType: isIntegrationTypeDisabled
                      ? FAKTUROWNIA_INTEGRATION_TYPES.invoice
                      : FAKTUROWNIA_INTEGRATION_TYPES.disabled,
                  });
                }}
              />
            </GridItem>
            <GridItem xs={12} md={6}>
              <GridContainer>
                <div className={classes.gridItemsWrapper}>
                  <GridItem className={classes.inputGridItem} md={12}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('brands.newBrandForm.host')}
                    </FormLabel>
                    <FormTextInputNoGrid
                      name="host"
                      value={module.host}
                      maxLength={64}
                      handleChange={e =>
                        handleModuleUpdate({
                          ...module,
                          host: e.target.value,
                        })
                      }
                    />
                  </GridItem>
                  <GridItem className={classes.inputGridItem} md={12}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('brands.newBrandForm.token')}
                    </FormLabel>
                    <FormTextInputNoGrid
                      name="token"
                      value={module.token}
                      maxLength={64}
                      handleChange={e =>
                        handleModuleUpdate({
                          ...module,
                          token: e.target.value,
                        })
                      }
                    />
                  </GridItem>
                  <GridItem className={classes.inputGridItem} md={12}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('brands.newBrandForm.department')}
                    </FormLabel>
                    <FormTextInputNoGrid
                      name="department"
                      value={module.department}
                      maxLength={64}
                      handleChange={e => {
                        if (!isNaN(e.target.value)) {
                          handleModuleUpdate({
                            ...module,
                            department: e.target.value,
                          });
                        }
                      }}
                    />
                  </GridItem>
                </div>

                <div className={classes.gridItemsWrapper}>
                  <GridItem className={classes.inputGridItem} md={12}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('module.Fakturownia.integrationType.label')}
                    </FormLabel>
                    <SelectInput
                      noGrid={true}
                      classes={{}}
                      mapBy="label"
                      trackBy="value"
                      name="integrationType"
                      value={module.integrationType}
                      options={[
                        {
                          label: t(
                            'module.Fakturownia.integrationOption.invoice'
                          ),
                          value: FAKTUROWNIA_INTEGRATION_TYPES.invoice,
                        },
                        {
                          label: t(
                            'module.Fakturownia.integrationOption.advance'
                          ),
                          value: FAKTUROWNIA_INTEGRATION_TYPES.advance,
                        },
                        {
                          label: t(
                            'module.Fakturownia.integrationOption.receipt'
                          ),
                          value: FAKTUROWNIA_INTEGRATION_TYPES.receipt,
                        },
                      ]}
                      handleChange={e =>
                        handleModuleUpdate({
                          ...module,
                          integrationType: e.target.value,
                        })
                      }
                      size={12}
                      disabled={isIntegrationTypeDisabled}
                    />
                  </GridItem>
                  {module.integrationType ===
                    FAKTUROWNIA_INTEGRATION_TYPES.receipt && (
                    <>
                      <GridItem className={classes.inputGridItem} md={12}>
                        <FormLabel className={classes.labelHorizontal}>
                          {t('brands.newBrandForm.minAmountInvoice')}
                        </FormLabel>
                        <FormTextInputNoGrid
                          name="minAmountInvoice"
                          value={module.minAmountInvoice}
                          maxLength={64}
                          handleChange={e => {
                            if (!isNaN(e.target.value)) {
                              handleModuleUpdate({
                                ...module,
                                minAmountInvoice: e.target.value,
                              });
                            }
                          }}
                        />
                      </GridItem>
                      <GridItem className={classes.inputGridItem}>
                        <FormLabel className={classes.labelHorizontal}>
                          {t('module.Fakturownia.receiptSharingMethod')}
                        </FormLabel>
                        <SelectInput
                          mapBy="label"
                          trackBy="value"
                          name="receiptSharingMethod"
                          value={module.receiptSharingMethod}
                          options={[
                            {
                              label: t(
                                'module.Fakturownia.receiptSharingMethod.pdf'
                              ),
                              value: 'PDF',
                            },
                            {
                              label: t(
                                'module.Fakturownia.receiptSharingMethod.eReceipt'
                              ),
                              value: 'ERECEIPT',
                            },
                          ]}
                          handleChange={e =>
                            handleModuleUpdate({
                              ...module,
                              receiptSharingMethod: e.target.value,
                            })
                          }
                          size={12}
                          disabled={isIntegrationTypeDisabled}
                        />
                      </GridItem>
                      <GridItem className={classes.inputGridItem} md={12}>
                        <LabeledCheckbox
                          className={classes.noRightMargin}
                          label={t(
                            'module.Fakturownia.integrationOption.autoPrint'
                          )}
                          checked={module.autoPrint}
                          onChange={() => {
                            handleModuleUpdate({
                              ...module,
                              autoPrint: !module.autoPrint,
                            });
                          }}
                        />
                        <Tooltip
                          title={
                            <h5>
                              {t(
                                'module.Fakturownia.addonsCustomName.printReceiptAutomaticallyTooltip'
                              )}
                            </h5>
                          }
                          placement="top"
                        >
                          <InfoIcon className={classes.infoIcon} />
                        </Tooltip>
                      </GridItem>
                    </>
                  )}
                </div>

                {module.integrationType ===
                  FAKTUROWNIA_INTEGRATION_TYPES.invoice && (
                  <div className={classes.gridItemsWrapper}>
                    <GridItem className={classes.inputGridItem} md={12}>
                      <FormLabel className={classes.labelHorizontal}>
                        {t(
                          'module.Fakturownia.integrationType.invoiceIssueDateType'
                        )}
                      </FormLabel>
                      <SelectInput
                        noGrid={true}
                        mapBy="label"
                        trackBy="value"
                        name="invoiceIssueDateType"
                        value={module.invoiceIssueDateType}
                        options={invoiceDatesOptions}
                        handleChange={e =>
                          handleModuleUpdate({
                            ...module,
                            invoiceIssueDateType: e.target.value,
                          })
                        }
                        size={12}
                        disabled={isIntegrationTypeDisabled}
                      />
                    </GridItem>

                    <GridItem className={classes.inputGridItem} md={12}>
                      <FormLabel className={classes.labelHorizontal}>
                        {t(
                          'module.Fakturownia.integrationType.invoiceSellDateType'
                        )}
                      </FormLabel>
                      <SelectInput
                        noGrid={true}
                        mapBy="label"
                        trackBy="value"
                        name="invoiceSellDateType"
                        value={module.invoiceSellDateType}
                        options={invoiceDatesOptions}
                        handleChange={e =>
                          handleModuleUpdate({
                            ...module,
                            invoiceSellDateType: e.target.value,
                          })
                        }
                        size={12}
                        disabled={isIntegrationTypeDisabled}
                      />
                    </GridItem>
                  </div>
                )}

                <div className={classes.gridItemsWrapper}>
                  <GridItem className={classes.inputGridItem} md={12}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('module.Fakturownia.dietIntegrationMode.label')}
                    </FormLabel>
                    <SelectInput
                      noGrid={true}
                      mapBy="label"
                      trackBy="value"
                      name="dietIntegrationMode"
                      value={module.dietIntegrationMode}
                      options={[
                        {
                          label: t(
                            'module.Fakturownia.dietIntegrationMode.DIET'
                          ),
                          value: 'DIET',
                        },
                        {
                          label: t(
                            'module.Fakturownia.dietIntegrationMode.BAGS'
                          ),
                          value: 'BAGS',
                        },
                      ]}
                      handleChange={e =>
                        handleModuleUpdate({
                          ...module,
                          dietIntegrationMode: e.target.value,
                        })
                      }
                      size={12}
                      disabled={isIntegrationTypeDisabled}
                    />
                  </GridItem>
                  <GridItem className={classes.inputGridItem} md={12}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('brands.newBrandForm.productName')}

                      <Tooltip
                        title={t('brands.newBrandForm.typeOfDiet')}
                        placement="top"
                      >
                        <InfoIcon className={classes.infoIcon} />
                      </Tooltip>
                    </FormLabel>
                    <div className={classes.checkboxWrapper}>
                      <LabeledCheckbox
                        checked={module.dietItemNameEnabled}
                        onChange={() => {
                          handleModuleUpdate({
                            ...module,
                            dietItemNameEnabled: !module.dietItemNameEnabled,
                          });
                        }}
                      />
                      <FormTextInputNoGrid
                        name="dietItemName"
                        value={module.dietItemName}
                        maxLength={255}
                        handleChange={e =>
                          handleModuleUpdate({
                            ...module,
                            dietItemName: e.target.value,
                          })
                        }
                      />
                    </div>
                  </GridItem>

                  <GridItem md={12}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('module.Fakturownia.addonsCustomName.label')}
                      <Tooltip
                        title={t('module.Fakturownia.addonsCustomName.toltip')}
                        placement="top"
                      >
                        <InfoIcon className={classes.infoIcon} />
                      </Tooltip>
                    </FormLabel>
                    <div className={classes.checkboxWrapper}>
                      <LabeledCheckbox
                        checked={module.addonsCustomNameEnabled}
                        onChange={() => {
                          handleModuleUpdate({
                            ...module,
                            addonsCustomNameEnabled:
                              !module.addonsCustomNameEnabled,
                          });
                        }}
                      />
                      <FormTextInputNoGrid
                        name="addonsCustomName"
                        value={module.addonsCustomName}
                        maxLength={255}
                        handleChange={e =>
                          handleModuleUpdate({
                            ...module,
                            addonsCustomName: e.target.value,
                          })
                        }
                      />
                    </div>
                  </GridItem>

                  <GridItem className={classes.inputGridItem} xs={12}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('brands.newBrandForm.dishNameOnInvoice')}
                    </FormLabel>
                    <div className={classes.checkboxWrapper}>
                      <LabeledCheckbox
                        checked={module.dishesCustomNameEnabled}
                        onChange={() => {
                          handleModuleUpdate({
                            ...module,
                            dishesCustomNameEnabled:
                              !module.dishesCustomNameEnabled,
                          });
                        }}
                      />
                      <FormTextInputNoGrid
                        name="dishesCustomName"
                        value={module.dishesCustomName}
                        maxLength={255}
                        handleChange={e =>
                          handleModuleUpdate({
                            ...module,
                            dishesCustomName: e.target.value,
                          })
                        }
                      />
                    </div>
                  </GridItem>

                  <GridItem className={classes.inputGridItem} xs={12}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('brands.newBrandForm.mealTypeNameOnInvoice')}
                    </FormLabel>
                    <div className={classes.checkboxWrapper}>
                      <LabeledCheckbox
                        checked={module.mealTypesCustomNameEnabled}
                        onChange={() => {
                          handleModuleUpdate({
                            ...module,
                            mealTypesCustomNameEnabled:
                              !module.mealTypesCustomNameEnabled,
                          });
                        }}
                      />
                      <FormTextInputNoGrid
                        name="mealTypesCustomName"
                        value={module.mealTypesCustomName}
                        maxLength={255}
                        handleChange={e =>
                          handleModuleUpdate({
                            ...module,
                            mealTypesCustomName: e.target.value,
                          })
                        }
                      />
                    </div>
                  </GridItem>
                </div>
              </GridContainer>
            </GridItem>

            <GridItem md={6}>
              <GridContainer>
                <div className={classes.gridItemsWrapper}>
                  <GridItem className={classes.inputGridItem} md={12}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('brands.newBrandForm.deliveryItem')}
                      <Tooltip
                        title={t('brands.newBrandForm.zoneDelivery')}
                        placement="top"
                      >
                        <InfoIcon className={classes.infoIcon} />
                      </Tooltip>
                    </FormLabel>
                    <FormTextInputNoGrid
                      name="deliveryItemName"
                      value={module.deliveryItemName}
                      maxLength={255}
                      handleChange={e =>
                        handleModuleUpdate({
                          ...module,
                          deliveryItemName: e.target.value,
                        })
                      }
                    />
                  </GridItem>
                  <GridItem className={classes.inputGridItem} md={12}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('brands.newBrandForm.dietDayMod')}
                    </FormLabel>
                    <FormTextInputNoGrid
                      name="bagModificationName"
                      value={module.bagModificationName}
                      maxLength={255}
                      handleChange={e =>
                        handleModuleUpdate({
                          ...module,
                          bagModificationName: e.target.value,
                        })
                      }
                    />
                  </GridItem>
                </div>
                <div className={classes.gridItemsWrapper}>
                  <GridItem className={classes.inputGridItem} md={12}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('module.Fakturownia.bagModificationVat.label')}
                    </FormLabel>

                    <FormTextInputNoGrid
                      name="bagModificationVat"
                      value={module.bagModificationVat}
                      maxLength={255}
                      handleChange={e => {
                        if (!isNaN(e.target.value)) {
                          handleModuleUpdate({
                            ...module,
                            bagModificationVat: e.target.value,
                          });
                        }
                      }}
                    />
                  </GridItem>
                  <GridItem className={classes.inputGridItem} md={12}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('module.Fakturownia.shopDishVat.label')}
                    </FormLabel>
                    <FormTextInputNoGrid
                      name="dishesVat"
                      value={module.dishesVat}
                      maxLength={255}
                      handleChange={e => {
                        if (!isNaN(e.target.value)) {
                          handleModuleUpdate({
                            ...module,
                            dishesVat: e.target.value,
                          });
                        }
                      }}
                    />
                  </GridItem>
                </div>
                <div className={classes.gridItemsWrapper}>
                  <GridItem className={classes.inputGridItem} md={12}>
                    <LabeledCheckbox
                      label={t(
                        'brands.newBrandForm.fakturownia.addCommentWithAmountOfUsedMoneyboxPoints'
                      )}
                      checked={module.showMoneyboxOnInvoice}
                      onChange={() => {
                        handleModuleUpdate({
                          ...module,
                          showMoneyboxOnInvoice: !module.showMoneyboxOnInvoice,
                        });
                      }}
                    />
                  </GridItem>
                  <GridItem md={12}>
                    <LabeledCheckbox
                      label={t('brands.newBrandForm.mergeSamePositions')}
                      checked={module.mergeSamePositions}
                      onChange={() => {
                        handleModuleUpdate({
                          ...module,
                          mergeSamePositions: !module.mergeSamePositions,
                        });
                      }}
                    />
                  </GridItem>
                  <GridItem md={12}>
                    <LabeledCheckbox
                      label={t(
                        'brands.newBrandForm.showPayuPaymentIdOnInvoice'
                      )}
                      checked={module.showPayuPaymentIdOnInvoice}
                      onChange={() => {
                        handleModuleUpdate({
                          ...module,
                          showPayuPaymentIdOnInvoice:
                            !module.showPayuPaymentIdOnInvoice,
                        });
                      }}
                    />
                  </GridItem>
                  <GridItem md={12}>
                    <LabeledCheckbox
                      label={t('brands.newBrandForm.showOrderNumberOnInvoice')}
                      checked={module.showOrderNumberOnInvoice}
                      onChange={() => {
                        handleModuleUpdate({
                          ...module,
                          showOrderNumberOnInvoice:
                            !module.showOrderNumberOnInvoice,
                        });
                      }}
                    />
                  </GridItem>
                </div>
              </GridContainer>
            </GridItem>
          </GridContainer>
        </CardBody>
      </Card>
    </>
  );
};

export default FakturowniaModule;
